import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import Button from '../../ui/Button';
import { Dialog } from './AccountBits';
import nurseryAccountsService from '../../../services/nurseryAccounts';
import { errorMessage } from '../../../utils/apiResponse';
import { money, num, round2 } from '../../../utils/nurseryMoney';
import { CREDIT_REASON_LABELS, MANUAL_CREDIT_REASONS, type CreditReason } from '../../../types/nurseryAccounts';

/**
 * Raise a credit note against an authorised invoice. Amounts are typed ex GST;
 * the total may not be more than the invoice still owes.
 */

const GST_RATE = 0.1;

interface Line {
  key: number;
  description: string;
  amount: string;
  gst: boolean;
}

export interface CreditNoteDialogProps {
  invoice: { id: number; invoice_number: string; balance: number | string };
  onClose: () => void;
  /** Called once the credit note has been raised. */
  onDone: () => void;
}

export function CreditNoteDialog({ invoice, onClose, onDone }: CreditNoteDialogProps) {
  const { t } = useTranslation('nursery', { keyPrefix: 'CreditNoteDialog' });
  const [reason, setReason] = useState<CreditReason>(MANUAL_CREDIT_REASONS[0]);
  const [lines, setLines] = useState<Line[]>([{ key: 1, description: '', amount: '', gst: true }]);
  const [nextKey, setNextKey] = useState(2);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const balance = num(invoice.balance);
  const subtotal = round2(lines.reduce((sum, l) => sum + round2(num(l.amount)), 0));
  const gst = round2(lines.reduce((sum, l) => sum + (l.gst ? round2(num(l.amount) * GST_RATE) : 0), 0));
  const total = round2(subtotal + gst);

  const complete = lines.every((l) => l.description.trim() && num(l.amount) > 0);
  const overBalance = total > balance + 0.005;

  const setLine = (key: number, patch: Partial<Line>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const addLine = () => {
    setLines((ls) => [...ls, { key: nextKey, description: '', amount: '', gst: true }]);
    setNextKey((k) => k + 1);
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryAccountsService.createCreditNote({
        invoice_id: invoice.id,
        reason,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        lines: lines.map((l) => ({ description: l.description.trim(), line_total: round2(num(l.amount)), gst: l.gst })),
      });
      if (res.success) {
        onDone();
        return;
      }
      setError(errorMessage(res, t('raiseFailed')));
    } catch (e) {
      setError(errorMessage(e, t('raiseFailed')));
    }
    setBusy(false);
  };

  return (
    <Dialog
      wide
      title={t('title')}
      subtitle={t('subtitle', { number: invoice.invoice_number })}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>{t('cancel')}</Button>
          <Button onClick={submit} disabled={!complete || overBalance || busy}>
            {busy ? t('working') : t('confirm')}
          </Button>
        </>
      }
    >
      <p className="text-sm text-neutral-700 break-words">
        {t('intro')}
      </p>
      {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 break-words">{error}</div>}

      <div>
        <label htmlFor="credit-reason" className="block text-sm font-medium text-neutral-700 mb-1">{t('reason')}</label>
        <select id="credit-reason" value={reason} onChange={(e) => setReason(e.target.value as CreditReason)}
          className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm">
          {MANUAL_CREDIT_REASONS.map((r) => <option key={r} value={r}>{t(`creditReason.${r}`, { defaultValue: CREDIT_REASON_LABELS[r] })}</option>)}
        </select>
      </div>

      <div className="space-y-3">
        {lines.map((l, i) => (
          <div key={l.key} className="border border-neutral-200 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-neutral-500">{t('line', { n: i + 1 })}</span>
              {lines.length > 1 && (
                <button type="button" aria-label={t('removeLine', { n: i + 1 })} className="text-red-500 hover:text-red-700 p-1"
                  onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}>
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
            <div>
              <label htmlFor={`credit-desc-${l.key}`} className="block text-sm font-medium text-neutral-700 mb-1">{t('description')}</label>
              <input id={`credit-desc-${l.key}`} type="text" value={l.description} maxLength={500}
                onChange={(e) => setLine(l.key, { description: e.target.value })}
                placeholder={t('descriptionPlaceholder')}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
              <div className="w-full sm:w-44">
                <label htmlFor={`credit-amount-${l.key}`} className="block text-sm font-medium text-neutral-700 mb-1">{t('amount')}</label>
                <input id={`credit-amount-${l.key}`} type="number" min="0" step="0.01" inputMode="decimal" value={l.amount}
                  onChange={(e) => setLine(l.key, { amount: e.target.value })}
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <label htmlFor={`credit-gst-${l.key}`} className="inline-flex items-center gap-2 text-sm text-neutral-700 pb-2">
                <input id={`credit-gst-${l.key}`} type="checkbox" checked={l.gst}
                  onChange={(e) => setLine(l.key, { gst: e.target.checked })} />
                {t('gstApplies')}
              </label>
            </div>
          </div>
        ))}
        <Button variant="outline" size="sm" type="button" onClick={addLine}>
          <Plus className="h-4 w-4 mr-1" /> {t('addLine')}
        </Button>
      </div>

      <dl className="bg-neutral-50 rounded-lg px-3 py-2 text-sm space-y-1">
        <div className="flex justify-between gap-3"><dt className="text-neutral-600">{t('subtotal')}</dt><dd>{money(subtotal)}</dd></div>
        <div className="flex justify-between gap-3"><dt className="text-neutral-600">{t('gst')}</dt><dd>{money(gst)}</dd></div>
        <div className="flex justify-between gap-3 font-semibold"><dt>{t('total')}</dt><dd>{money(total)}</dd></div>
        <div className="flex justify-between gap-3 pt-1 border-t border-neutral-200">
          <dt className="text-neutral-600">{t('stillOwing')}</dt><dd>{money(balance)}</dd>
        </div>
      </dl>
      {overBalance && (
        <p className="text-sm text-red-700 break-words" role="alert">
          {t('overBalance', { total: money(total), balance: money(balance), excess: money(round2(total - balance)) })}
        </p>
      )}

      <div>
        <label htmlFor="credit-notes" className="block text-sm font-medium text-neutral-700 mb-1">{t('notes')}</label>
        <textarea id="credit-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
          className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
      </div>
    </Dialog>
  );
}

export default CreditNoteDialog;
